<template>
  <Header/>
  <main>
    <div class="container">
      <div class="row">
        <div class="col-12 m-0 no-max-w home-div">
          <Toast/>
          <ConfirmDialog/>
          <DataTable :value="contents" :lazy="true" :paginator="true"
                     paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                     :rowsPerPageOptions="[5, 10, 20, 50, 100]" dataKey="id" :rows="10" ref="dt"
                     :totalRecords="contentsTotalRecords" :loading="loading" @page="onContentsPage($event)"
                     @sort="onContentsSort($event)" @filter="onContentsFilter($event)" responsiveLayout="scroll"
                     class="p-datatable-striped" v-model:filters="filters" filterDisplay="menu" removableSort
                     :globalFilterFields="['global', 'name', 'isActive', 'relationshipStatusIds']"
                     currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">
            <template #header>
              <div class="d-flex justify-content-between">
                <div>
                  <Button type="button" icon="pi pi-plus" label="Add Content" class="p-button-info d-inline me-2"
                          @click="showNewContentModal = true"/>
                  <Button icon="pi pi-external-link" label="Export" @click="exportCSV($event)"/>
                  <Dropdown v-model="selectedContentType" :options="contentTypes" optionLabel="name" optionValue="id"
                            @change="getContents" class="ms-2" placeholder="Select Content Type"/>
                </div>
                <div>
                  <Button type="button" icon="pi pi-filter-slash" label="Clear" class="p-button-outlined d-inline me-2"
                          @click="clearFilter()"/>
                  <span class="p-input-icon-left">
                    <i class="pi pi-search"/>
                    <InputText v-model="filters['global'].value" placeholder="Keyword Search" @change="getContents"/>
                  </span>
                </div>
              </div>
            </template>

            <template #empty> No daily contents found.</template>
            <template #loading> Loading daily contents data. Please wait.</template>
            <Column field="id" header="Id" ref="id">
              <template #body="slotProps">
                <span class="m-0">{{ slotProps.data.id }}</span>
              </template>
            </Column>
            <Column field="title" header="Title" ref="title">
              <template #body="slotProps">
                <span class="m-0">{{ slotProps.data.title }}</span>
              </template>
            </Column>
            <Column field="text" header="Text" ref="text">
              <template #body="slotProps">
                <ScrollPanel style="width: 100%; height: 100px" class="custombar">
                  <span class="m-0">{{ slotProps.data.text }}</span>
                </ScrollPanel>
              </template>
            </Column>
            <Column field="type" header="Type"/>
            <Column field="house" header="House" ref="house" :show-filter-match-modes="false">
              <template #body="slotProps">
                <span class="m-0">{{ this.houses.find(x => x.id == slotProps.data.houseId)?.name }} </span>
              </template>
              <template #filter="">
                <MultiSelect v-model="filters['house'].value" :options="houses" optionLabel="name" optionValue="id"
                             class="p-column-filter"/>
              </template>
            </Column>
            <Column field="score" header="Score" ref="score">
              <template #body="slotProps">
                <span class="m-0">{{ slotProps.data.score }}</span>
              </template>
              <template #filter="">
                <Slider v-model="filters['score'].value" range max="10" min="0" class="mb-2"/>
                <span class="mt-2">{{ filters['score'].value[0] }} - {{ filters['score'].value[1] }}</span>
              </template>
            </Column>
            <Column field="gender" header="Gender" ref="gender">
              <template #body="slotProps">
                <span class="m-0">{{ this.genders.find(x => x.id == slotProps.data.gender)?.name }}</span>
              </template>
              <template #filter="">
                <MultiSelect v-model="filters['gender'].value" :options=genders optionLabel="name" optionValue="id"
                             class="p-column-filter"/>
              </template>
            </Column>
            <Column field="relationshipStatusIds" ref="relationshipStatusIds" header="RelationshipStatus">
              <template #body="slotProps">
                <div
                    v-if="this.relationshipIds.every(x => slotProps.data.relationshipStatusIds.includes(x)) || slotProps.data.relationshipStatusIds.length === 0">
                  <span class="badge bg-primary rounded-pill">All</span>
                </div>
                <div v-else>
                  <ScrollPanel style="width: 100%; height: 100px" class="custombar">
                    <div v-for="relationshipId in slotProps.data.relationshipStatusIds" :key="relationshipId">
                      <span class="mt-1">{{ this.relationships?.find(x => x.id == relationshipId)?.name }}</span>
                    </div>
                  </ScrollPanel>
                </div>
              </template>
              <template #filter="">
                <MultiSelect v-model="filters['relationshipStatusIds'].value" :options=relationships optionLabel="name"
                             optionValue="id" class="p-column-filter" :maxSelectedLabels="3"/>
              </template>
            </Column>
            <Column field="occupation" header="Occupations" ref="slug">
              <template #body="slotProps">
                <div
                    v-if="this.occupationIds.every(x => slotProps.data.occupationIds.includes(x)) || slotProps.data.occupationIds.length === 0">
                  <span class="badge bg-primary rounded-pill">All</span>
                </div>
                <div v-else>
                  <ScrollPanel style="width: 100%; height: 100px" class="custombar">
                    <div v-for="occupationId in slotProps.data.occupationIds" :key="occupationId">
                      <span class="mt-1">{{ this.occupations?.find(x => x.id == occupationId)?.name }}</span>
                    </div>
                  </ScrollPanel>
                </div>
              </template>
              <template #filter="">
                <MultiSelect v-model="filters['occupation'].value" :options=occupations optionLabel="name"
                             optionValue="id" class="p-column-filter" :maxSelectedLabels="3"/>
              </template>
            </Column>
            <Column field="minAge" header="Min Age" ref="minAge">
              <template #body="slotProps">
                <span class="m-0">{{ slotProps.data.minAge }}</span>
              </template>
              <template #filter="">
                <InputNumber v-model="filters['minAge'].value"/>
              </template>
            </Column>
            <Column field="maxAge" header="Max Age" ref="maxAge">
              <template #body="slotProps">
                <span class="m-0">{{ slotProps.data.maxAge }}</span>
              </template>
              <template #filter="">
                <InputNumber v-model="filters['maxAge'].value"/>
              </template>
            </Column>
            <Column field="actions" header="Actions" ref="actions">
              <template #body="slotProps">
                <Button v-tooltip.left="'Edit Translations'"
                    icon="pi pi-book"
                    class="p-button p-button-sm me-2 my-2"
                    @click="showEditLanguage(slotProps.data.id)"
                />
                <Button v-tooltip.left="'Edit Content'" icon="pi pi-pencil" class="p-button-warning p-button-sm me-2 my-2"
                        @click="openEditContent(slotProps.data.id)"/>
                <Button v-tooltip.left="'Delete Content'" icon="pi pi-trash" class="p-button-danger p-button-sm me-2 my-2"
                        @click="deleteContent(slotProps.data.id)"/>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>

      <AddDailyContentModal :content-type="selectedContentType" :show="showNewContentModal"
                            @hide="showNewContentModal = false" @refresh="getContents"/>

      <TranslationsModal
          :show="showEditLanguageModal"
          @hide="showEditLanguageModal = false"
          :contentId="editTranslationContentId"
      />

      <Dialog header="Edit Content" v-model:visible="showEditContent" modal :style="{ 'width': '40%' }">
        <div class="row mb-3 mt-3">
          <div class="col-md-6 form-group">
            <label class="d-block">General:</label>
            <InputSwitch v-model="editContentModel.isGeneral"/>
          </div>
          <div class="col-md-6 form-group" v-if="editContentModel.isGeneral">
            <label class="d-block">Date:</label>
            <Datepicker v-model="editContentModel.startDate" format="dd/MM/yyyy"></Datepicker>
          </div>
          <div v-if="!editContentModel.isGeneral" class="col-md-6 form-group">
            <label class="d-block">House:</label>
            <Dropdown v-model="editContentModel.houseId" :options="houses" optionLabel="name" optionValue="id"
                      placeholder="Select House"/>
          </div>
          <div class="col-md-12 form-group">
            <label class="d-block">Score:</label>
            <InputNumber class="w-50" mode="decimal" v-model="editContentModel.score"/>
          </div>
          <div class="col-md-6 form-group">
            <label class="d-block">Min Age:</label>
            <InputNumber class="w-100" mode="decimal" v-model="editContentModel.minAge"/>
          </div>
          <div class="col-md-6 form-group">
            <label class="d-block">Max Age:</label>
            <InputNumber class="w-100" mode="decimal" v-model="editContentModel.maxAge"/>
          </div>
          <div class="col-md-6 form-group">
            <label class="d-block">Gender:</label>
            <Dropdown class="w-100" :options="genders" v-model="editContentModel.gender" optionLabel="name"
                      optionValue="id"/>
          </div>
          <div class="col-md-6 form-group">
            <label class="d-block">Relationship:</label>
            <MultiSelect class="w-100" :options="relationships" v-model="editContentModel.relationshipStatusIds"
                         optionLabel="name" optionValue="id"/>
          </div>
          <div class="col-md-12 form-group">
            <label class="d-block">Occupations:</label>
            <MultiSelect class="w-100" v-model="editContentModel.occupationIds" :options="occupations"
                         optionLabel="name"
                         optionValue="id" placeholder="Select Occupations"/>
          </div>
        </div>
        <div class="col-md-12 form-group">
          <label class="d-block">Categories:</label>
          <MultiSelect class="w-100" v-model="editContentModel.categoryIds" :options="categories" optionLabel="name"
                       optionValue="id" placeholder="Select Categories"/>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-default" @click="showEditContent = false">
            Close
          </button>
          <Button label="Save" icon="pi pi-save" iconPos="left" @click="editContent()"/>
        </div>
      </Dialog>
    </div>
  </main>
</template>

<script>
import Header from "@/components/Header.vue";
import dayjs from "dayjs";
import "@vuepic/vue-datepicker/dist/main.css";
import InputText from "primevue/inputtext";
import {mapGetters} from "vuex";
import TranslationsModal from "../Modals/TranslationsModal.vue";

export default {
  name: "Daily Contents",
  components: {
    Header,
    InputText,
    TranslationsModal,
  },
  data() {
    return {
      selectedContentType: 'daily',
      contentTypes: [
        {id: "daily", name: "daily"},
        {id: "daily-suggestion", name: "daily-suggestion"},
      ],
      loading: false,
      contentsTotalRecords: 0,
      contents: [],
      filters: {
        global: {value: null},
        name: {value: null},
        house: {value: null},
        gender: {value: null},
        relationshipStatusIds: {value: null},
        score: {value: []},
        occupation: {value: null},
        minAge: {value: null},
        maxAge: {value: null},
      },
      contentsLazyParams: {},
      showNewContentModal: false,
      showEditLanguageModal: false,
      showEditContent: false,
      newContent: {
        title: "",
        text: "",
        score: 1,
        houseId: null,
        ascendantSignId: null,
        gender: 0,
        minAge: 0,
        maxAge: 100,
        relationshipStatus: 0,
      },
      editContentModel: {},
      messages: [],
      messageCount: 0,
      editLanguageModel: {},
      genders: [{id: 0, name: "All"}, {id: 1, name: "Man"}, {id: 2, name: "Woman"}],
      editTranslationContentId: null,
    };
  },
  mounted() {
    this.contentsLazyParams = {
      first: 0,
      rows: this.$refs.dt.rows,
      sortField: null,
      sortOrder: null,
      filters: this.filters,
    };
    this.getContents();
  },
  computed: {
    ...mapGetters(['occupations', 'signs', 'houses', 'relationships', 'occupationIds', 'relationshipIds', 'categories']),
  },
  watch: {},
  methods: {
    getContents() {
      this.loading = true;
      var filters = {};

      for (const [key] of Object.entries(this.contentsLazyParams.filters)) {
        filters[key] = this.contentsLazyParams.filters[key].value;
      }
      this.$appAxios
          .post("admin/content/list/" + this.selectedContentType, {
            first: this.contentsLazyParams.first,
            rows: this.contentsLazyParams.rows,
            sortField: this.contentsLazyParams.sortField,
            sortOrder: this.contentsLazyParams.sortOrder,
            page: this.contentsLazyParams.page,
            filters: JSON.stringify(filters),
          })
          .then((resp) => {
            this.contents = resp.data.data;
            this.contentsTotalRecords = resp.data.totalRecords;
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
    },
    onContentsPage(event) {
      this.contentsLazyParams = event;
      this.contentsLazyParams.filters = this.filters;
      this.getContents();
    },
    onContentsSort(event) {
      this.contentsLazyParams = event;
      this.contentsLazyParams.filters = this.filters;
      this.getContents();
    },
    onContentsFilter() {
      this.contentsLazyParams.filters = this.filters;
      this.getContents();
    },
    clearFilter() {
      for (const [key] of Object.entries(this.filters)) {
        this.filters[key].value = null;
      }
      this.getContents();
    },
    formatDate(dateString) {
      if (dateString == null) {
        return "";
      }

      const date = dayjs(dateString);
      return date.format("DD/MM/YYYY HH:mm");
    },
    exportCSV() {
      this.$refs.dt.exportCSV();
    },
    showEditLanguage(id) {
      this.editTranslationContentId = id;
      this.showEditLanguageModal = true;
    },
    editLanguage() {
      if (!(this.editLanguageModel.text && this.editLanguageModel.language)) {
        this.messages = [{severity: 'warn', content: 'Required fields must be entered.', id: this.messageCount++}];
        return;
      }
      this.$appAxios.put("admin/content/translation",
          {
            id: this.editLanguageModel.id,
            title: this.editLanguageModel.title,
            text: this.editLanguageModel.text,
            language: this.editLanguageModel.language,
            isVerified: this.editLanguageModel.isVerified,
          }
      ).then(() => {
        this.showEditLanguageModal = false;
        this.$toast.add({severity: 'success', summary: 'Success', detail: 'Language info updated', life: 3000});
        this.getContents();
      })
          .catch(() => {
            this.showEditLanguageModal = false;
            this.$toast.add({severity: 'error', summary: 'Error', detail: 'Language info not updated', life: 3000});
          })
    },
    openEditContent(id) {
      this.$appAxios.get("admin/content/" + id)
          .then(resp => {
            this.editContentModel = resp.data;
            this.showEditContent = true;
          })
          .catch(err => {
            console.log(err)
          });
    },
    editContent() {
      this.$appAxios.put("admin/content",
          {
            id: this.editContentModel.id,
            ascendantSignId: this.editContentModel.ascendantSignId,
            houseId: this.editContentModel.houseId,
            gender: this.editContentModel.gender,
            minAge: this.editContentModel.minAge,
            maxAge: this.editContentModel.maxAge,
            score: this.editContentModel.score,
            relationshipStatusIds: this.editContentModel.relationshipStatusIds,
            occupationIds: this.editContentModel.occupationIds,
            type: this.selectedContentType,
            isGeneral: this.editContentModel.isGeneral,
            startDate: this.editContentModel.startDate,
            endDate: this.editContentModel.startDate,
            categoryIds: this.editContentModel.categoryIds,
          }
      ).then(() => {
        this.showEditContent = false;
        this.$toast.add({severity: 'success', summary: 'Success', detail: 'Content updated', life: 3000});
        this.getContents();
      })
          .catch(() => {
            this.showEditContent = false;
            this.$toast.add({severity: 'error', summary: 'Error', detail: 'Content not updated', life: 3000});
          })
    },
    deleteContent(id) {
      this.$confirm.require({
        message: 'Are you sure you want to delete the content?',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.$appAxios.delete("admin/content/" + id)
              .then(() => {
                this.$toast.add({severity: 'success', summary: 'Success', detail: 'Content deleted', life: 3000});
                this.getContents();
              })
              .catch(() => {
                this.$toast.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Content could not be deleted',
                  life: 3000
                });
              })
        },
        reject: () => {
        }
      });
    },
  },
};
</script>
<style scoped>
.p-button-sm {
  height: 35px;
  width: 35px;
  font-size: 0.875rem;
  padding: 0.7rem;
}

.p-disabled,
.p-component:disabled {
  opacity: 0.8;
}

.p-dropdown {
  min-width: 200px;
}
</style>
